import React, { useState } from "react";
import { useLiveSession } from "./useLiveSession";

const COL = {
  bg: "#0d1420",
  panel: "#131722",
  border: "#1a2535",
  text: "#e8f0f8",
  dim: "#8aabb8",
  accent: "#00d4ff",
  green: "#00e5a0",
  red: "#ff3f50",
};

/**
 * Floating Live Session widget — chat + gedeelde cycle-sync met coach/studenten.
 * cycleStore: het object van useCycleStore() uit App.js, uitgebreid met
 * broadcastRef + applyRemoteCycles (zie App.js wijzigingen).
 */
export default function LiveSessionPanel({
  cycleStore,
  defaultRoom = "wyckoffcafe-live",
  shareLabel = "Your drawings are shared live in this room.",
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(() => localStorage.getItem("wc_live_name") || "");
  const [role, setRole] = useState(() => localStorage.getItem("wc_live_role") || "student");
  const [room, setRoom] = useState(() => localStorage.getItem("wc_live_room") || defaultRoom);
  const [chatInput, setChatInput] = useState("");

  const { connected, connecting, chat, participants, connect, disconnect, sendChat } =
    useLiveSession({ room, name, role, cycleStore });

  const participantCount = Object.keys(participants).length + (connected ? 1 : 0);

  const handleConnect = () => {
    if (!name.trim() || !room.trim()) return;
    localStorage.setItem("wc_live_name", name.trim());
    localStorage.setItem("wc_live_role", role);
    localStorage.setItem("wc_live_room", room.trim());
    connect();
  };

  const handleSend = () => {
    if (!chatInput.trim()) return;
    sendChat(chatInput.trim());
    setChatInput("");
  };

  return (
    <div style={{ position: "fixed", right: 16, bottom: 16, zIndex: 9999, fontFamily: "'Courier New',monospace" }}>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          style={{
            padding: "8px 14px",
            borderRadius: 6,
            cursor: "pointer",
            background: connected ? COL.green : COL.panel,
            color: connected ? "#06130d" : COL.dim,
            border: `1px solid ${connected ? COL.green : COL.border}`,
            fontWeight: 900,
            fontSize: 11,
            letterSpacing: "0.06em",
            boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
          }}
        >
          {connected ? `🟢 LIVE · ${participantCount}` : "⚫ Live Session"}
        </button>
      )}

      {open && (
        <div
          style={{
            width: 290,
            background: COL.bg,
            border: `1px solid ${COL.border}`,
            borderRadius: 8,
            boxShadow: "0 8px 28px rgba(0,0,0,0.55)",
            display: "flex",
            flexDirection: "column",
            maxHeight: 440,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 10px",
              borderBottom: `1px solid ${COL.border}`,
              background: "rgba(0,212,255,0.05)",
            }}
          >
            <span style={{ fontSize: 10, fontWeight: 900, color: COL.accent, letterSpacing: "0.1em" }}>
              LIVE SESSION {connected && <span style={{ color: COL.green }}>●</span>}
            </span>
            <span onClick={() => setOpen(false)} style={{ cursor: "pointer", color: COL.dim, fontSize: 12 }}>
              ✕
            </span>
          </div>

          {!connected ? (
            <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 6 }}>
              <input
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={inputStyle}
              />
              <select value={role} onChange={(e) => setRole(e.target.value)} style={inputStyle}>
                <option value="coach">Coach</option>
                <option value="student">Student</option>
              </select>
              <input
                placeholder="Room code (share this with your students)"
                value={room}
                onChange={(e) => setRoom(e.target.value)}
                style={inputStyle}
              />
              <button
                onClick={handleConnect}
                disabled={!name.trim() || !room.trim() || connecting}
                style={{
                  padding: "7px 0",
                  borderRadius: 3,
                  cursor: name.trim() && room.trim() ? "pointer" : "not-allowed",
                  background: "rgba(0,212,255,0.13)",
                  border: `1px solid ${COL.accent}`,
                  color: COL.accent,
                  fontSize: 11,
                  fontWeight: 900,
                  letterSpacing: "0.06em",
                }}
              >
                {connecting ? "Connecting…" : "Connect"}
              </button>
            </div>
          ) : (
            <>
              <div
                style={{
                  padding: "4px 10px",
                  fontSize: 9,
                  color: COL.dim,
                  borderBottom: `1px solid ${COL.border}`,
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <span>
                  {participantCount} participant{participantCount !== 1 ? "s" : ""} · room "{room}"
                </span>
                <span onClick={disconnect} style={{ color: COL.red, cursor: "pointer" }}>
                  leave
                </span>
              </div>

              <div style={{ flex: 1, overflowY: "auto", padding: "6px 10px", minHeight: 130, maxHeight: 220 }}>
                {chat.length === 0 && (
                  <div style={{ fontSize: 9, color: COL.dim }}>No messages yet.</div>
                )}
                {chat.map((m, i) => (
                  <div key={i} style={{ fontSize: 10, marginBottom: 4, lineHeight: 1.4 }}>
                    <span style={{ color: COL.accent, fontWeight: 700 }}>{m.from}: </span>
                    <span style={{ color: COL.text }}>{m.text}</span>
                  </div>
                ))}
              </div>

              <div style={{ display: "flex", gap: 4, padding: "6px 10px", borderTop: `1px solid ${COL.border}` }}>
                <input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSend();
                  }}
                  placeholder="Message..."
                  style={{ ...inputStyle, flex: 1 }}
                />
                <button
                  onClick={handleSend}
                  disabled={!chatInput.trim()}
                  title="Send"
                  style={{
                    padding: "0 10px",
                    borderRadius: 3,
                    cursor: chatInput.trim() ? "pointer" : "not-allowed",
                    background: "rgba(0,212,255,0.13)",
                    border: `1px solid ${COL.accent}`,
                    color: COL.accent,
                    fontSize: 13,
                    fontWeight: 900,
                    opacity: chatInput.trim() ? 1 : 0.5,
                  }}
                >
                  ➤
                </button>
              </div>

              <div style={{ fontSize: 8, color: COL.dim, padding: "4px 10px", borderTop: `1px solid ${COL.border}` }}>
                {shareLabel}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const inputStyle = {
  background: "#1c2030",
  border: `1px solid ${COL.border}`,
  color: COL.text,
  padding: "6px 8px",
  borderRadius: 3,
  fontSize: 11,
  fontFamily: "'Courier New',monospace",
  outline: "none",
};
